import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components';
import { Provider, getProviderById } from '@/services/api/providers';
import { toast } from '@/design-system/toaster';
import { useUrlParam } from '@/hooks/urlState';

import ProviderDetailTab from './components/ProviderDetailTab';
import ProviderUsersTab from './components/ProviderUsersTab';
import ProviderConfigTab from './components/ProviderConfigTab';
import ProviderClientsTab from './components/ProviderClientsTab';
import { BUSINESS_UNIT_LABELS } from './index';

type DetailTabKey =
  | 'detail'
  | 'pricing'
  | 'users'
  | 'configuration'
  | 'clients';

const TABS: {
  key: DetailTabKey;
  label: string;
  icon: string;
  locked?: boolean;
  disabled?: boolean;
}[] = [
  { key: 'detail', label: 'Company Profile', icon: 'ri-building-line' },
  {
    key: 'pricing',
    label: 'Pricing Schemes',
    icon: 'ri-money-dollar-circle-line',
    locked: true,
    disabled: true,
  },
  { key: 'users', label: 'User Management', icon: 'ri-team-line' },
  { key: 'configuration', label: 'Configuration', icon: 'ri-settings-4-line' },
  { key: 'clients', label: 'Sub-Clients', icon: 'ri-user-shared-line' },
];

const TAB_KEYS = TABS.map((t) => t.key) as readonly DetailTabKey[];

export const ProviderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useUrlParam<DetailTabKey>('tab', 'detail', {
    allowed: TAB_KEYS,
    history: 'replace',
  });

  const fetchProvider = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getProviderById(id);
      setProvider(data);
    } catch (err: any) {
      toast.error('Failed to load client details', {
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProvider();
  }, [fetchProvider]);

  if (loading && !provider) {
    return (
      <div className="space-y-5 p-6">
        <div className="h-8 w-48 bg-bg-weak-50 animate-pulse rounded-gsm" />
        <div className="h-64 bg-bg-weak-50 animate-pulse rounded-gsm" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="space-y-5 p-6">
        <div className="rounded-gsm border border-stroke-soft-200 bg-white p-12 text-center">
          <i className="ri-error-warning-line text-4xl text-text-sub-600/50 mb-3" />
          <h2 className="text-lg font-bold text-text-strong-950">
            Client Not Found
          </h2>
          <p className="text-sm text-text-sub-600 mt-1 mb-6">
            The requested client record does not exist or you do not have permission to view it.
          </p>
          <Button variant="white" onClick={() => navigate('/providers')}>
            <i className="ri-arrow-left-line mr-1.5" />
            Back to Clients
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <header className="flex flex-col gap-4 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate('/providers')}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-text-sub-600 hover:text-text-strong-950 transition-colors mb-2"
          >
            <i className="ri-arrow-left-line text-[14px]" />
            Back to Clients
          </button>
          <p className="label-eyebrow mb-1">
            <span className="text-primary font-bold mr-3">01</span>
            Master · Client Detail
          </p>
          <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950 leading-none">
            {provider.name}
          </h1>
          <p className="mt-2 text-sm text-text-sub-600 leading-relaxed font-mono">
            {provider.code ? `${provider.code} · ` : ''}
            {provider.revenue_stream
              ? (BUSINESS_UNIT_LABELS[provider.revenue_stream] ?? provider.revenue_stream)
              : '—'}
            {provider.legal_name ? ` · ${provider.legal_name}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="white" onClick={fetchProvider}>
            <i className="ri-refresh-line mr-1.5 text-[14px]" />
            Refresh
          </Button>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="flex flex-wrap items-center border-b border-stroke-soft-200 -mb-px gap-1">
        {TABS.map((t) => {
          const isActive = activeTab === t.key;
          const isDisabled = t.disabled || t.locked;
          return (
            <button
              key={t.key}
              type="button"
              disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) {
                  setActiveTab(t.key);
                }
              }}
              className={`inline-flex items-center gap-2 px-3.5 py-2.5 text-[13px] font-semibold tracking-tight transition-colors border-b-2 select-none ${
                isDisabled
                  ? 'cursor-not-allowed text-text-sub-600/50 border-transparent opacity-60'
                  : isActive
                  ? 'border-primary text-text-strong-950 font-bold'
                  : 'border-transparent text-text-sub-600 hover:text-text-strong-950'
              }`}
            >
              <i className={`${t.icon} text-[15px]`} />
              <span>{t.label}</span>
              {t.locked && (
                <i
                  className="ri-lock-line text-[14px] text-text-sub-600/70 ml-0.5"
                  aria-label="Locked"
                  title="Locked"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content Panels */}
      <div>
        {activeTab === 'detail' && (
          <ProviderDetailTab provider={provider} onRefresh={fetchProvider} />
        )}
        {activeTab === 'pricing' && <div className="min-h-[200px]" />}
        {activeTab === 'users' && (
          <ProviderUsersTab providerId={provider.id} />
        )}
        {activeTab === 'configuration' && (
          <ProviderConfigTab providerId={provider.id} />
        )}
        {activeTab === 'clients' && (
          <ProviderClientsTab providerId={provider.id} />
        )}
      </div>
    </div>
  );
};

export default ProviderDetailPage;
